import { useMemo } from "react";
import { useT } from "@sablier/locales";
import { DEFAULT_CHAIN_ID, StreamShapes } from "@sablier/v2-constants";
import { guards } from "@sablier/v2-machines";
import { BigNumber, _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { useRouter } from "next/router";
import { supported } from "~/client/constants";
import { useAccount, useTokens } from "~/client/hooks";
import type { IAutofillParams, ITiming } from "@sablier/v2-types";

type Warning =
  | {
      state: "ready";
      value: IAutofillParams;
    }
  | {
      state: "numeric";
      purpose: string;
      value?: string;
    }
  | {
      state: "connect";
    }
  | {
      state: "chain";
      chainId?: number;
    }
  | {
      state: "token";
      chainId?: number;
      address?: string;
    }
  | {
      state: "timing";
    };

function param(query: object, key: string, length = 42, expected?: "seconds") {
  const value = _.get(query, key);
  const sliced = !_.isNilOrEmptyString(value)
    ? _.toString(value).toLowerCase().slice(0, length)
    : undefined;

  if (_.isNilOrEmptyString(sliced)) {
    return sliced;
  }

  if (expected === "seconds") {
    return _.toValuePrepared({ humanized: sliced, decimals: 3 });
  }

  return sliced;
}

function useParams() {
  const { chainId: currentChainId } = useAccount();
  const router = useRouter();
  const { query } = router;

  const { find: findToken } = useTokens();

  return useMemo(() => {
    const identifier = param(query, "shape");
    const shape = Stream.findShape(identifier);

    const recipient = param(query, "recipient");
    const amount = param(query, "amount");

    const chainId =
      (() => {
        const queryChainId = param(query, "chainId", 10) || 0;
        const queryChainIdBN = new BigNumber(queryChainId);
        if (queryChainId && queryChainIdBN.isFinite()) {
          if (
            supported.find((chain) => chain.id === queryChainIdBN.toNumber())
          ) {
            return queryChainIdBN.toNumber();
          }
        }
        return currentChainId;
      })() || DEFAULT_CHAIN_ID;

    const timing: ITiming = (() => {
      const isDuration = param(query, "isDuration", 5);

      if (!_.isNilOrEmptyString(isDuration)) {
        /** Default isDuration flag to true */
        const flag = ![false, "false"].includes(isDuration);
        return flag ? "duration" : "range";
      }

      return "duration";
    })();

    const tokenAddress = param(query, "token");
    const token = findToken({ address: tokenAddress, chainId });

    /** Default cancelability to true */
    const cancelable = ![false, "false"].includes(
      param(query, "cancelable", 5) || "",
    );
    const duration =
      timing === "duration"
        ? param(query, "duration", 12, "seconds")
        : undefined;
    const end =
      timing === "range" ? param(query, "end", 12, "seconds") : undefined;
    const start =
      timing === "range" ? param(query, "start", 12, "seconds") : undefined;

    const cliff = [
      StreamShapes.cliff.id,
      StreamShapes.dynamicCliffExponential.id,
    ].includes(shape.id)
      ? param(query, "cliff", 12, "seconds")
      : undefined;
    const steps = [StreamShapes.dynamicStepper.id].includes(shape.id)
      ? param(query, "steps", 4)
      : undefined;

    const unlock = [
      StreamShapes.dynamicUnlockCliff.id,
      StreamShapes.dynamicUnlockLinear,
    ].includes(shape.id)
      ? param(query, "unlock", 4)
      : undefined;

    return {
      amount,
      cancelable,
      chainId,
      cliff,
      duration,
      end,

      recipient,
      shape,
      start,
      steps,
      unlock,
      timing,

      token,
      tokenAddress,
    };
  }, [currentChainId, findToken, query]);
}

export function useAutofillWarning(): Warning {
  const { chainId: fromChainId, isConnected } = useAccount();
  const { t } = useT();
  const params = useParams();

  return useMemo(() => {
    const {
      amount,
      cancelable,
      chainId,
      cliff,
      duration,
      end,
      recipient,
      shape,
      start,
      steps,
      timing,
      token,
      tokenAddress,
      unlock,
    } = params;

    const proofed: IAutofillParams = {
      cancelable,
      recipient,
      shape,
      timing,
    };

    if (!isConnected) {
      return { state: "connect" };
    }

    /** Do not allow empty or unsupported chains */
    if (_.isNilOrEmptyString(chainId) || fromChainId !== _.toNumber(chainId)) {
      return { state: "chain", chainId: _.toNumber(chainId) };
    }

    proofed.chainId = chainId;

    /** If token present, allow only whitelisted tokens  */
    if (!_.isNilOrEmptyString(tokenAddress) && _.isNilOrEmptyString(token)) {
      return {
        state: "token",
        address: tokenAddress,
        chainId: _.toNumber(chainId),
      };
    }

    proofed.token = token;

    /** If an amount is present, allow only valid, non-scientific numbers */
    if (!_.isNilOrEmptyString(amount)) {
      if (guards.validateAmount({ t, value: amount }) !== undefined) {
        return { state: "numeric", purpose: "amount", value: amount };
      }
    }

    proofed.amount = amount;

    if (timing === "duration") {
      /** If an duration is present, allow only valid, non-scientific numbers */
      if (!_.isNilOrEmptyString(duration)) {
        /** Use the more complex validation here to check for numeric formats */
        if (guards.validateAmount({ t, value: duration }) !== undefined) {
          return { state: "numeric", purpose: "duration", value: duration };
        }

        proofed.duration = duration;
      }
    }

    if (timing === "range") {
      if (!_.isNilOrEmptyString(start)) {
        if (guards.validateAmount({ t, value: start }) !== undefined) {
          return { state: "numeric", purpose: "start date", value: start };
        }
        proofed.start = start;
      }

      if (!_.isNilOrEmptyString(end)) {
        if (guards.validateAmount({ t, value: end }) !== undefined) {
          return { state: "numeric", purpose: "end date", value: end };
        }
        proofed.end = end;
      }
    }

    if (["duration", "range"].includes(timing || "")) {
      if (
        [
          StreamShapes.cliff.id,
          StreamShapes.dynamicCliffExponential.id,
        ].includes(shape.id)
      ) {
        if (!_.isNilOrEmptyString(cliff)) {
          if (guards.validateAmount({ t, value: cliff }) !== undefined) {
            return { state: "numeric", purpose: "cliff", value: cliff };
          }
          proofed.cliff = cliff;
        }
      }
    }

    if ([StreamShapes.dynamicStepper.id].includes(shape.id)) {
      if (!_.isNilOrEmptyString(steps)) {
        if (guards.validateAmount({ t, value: steps }) !== undefined) {
          return { state: "numeric", purpose: "steps", value: steps };
        }
        proofed.steps = steps;
      }
    }

    if (
      [
        StreamShapes.dynamicUnlockCliff.id,
        StreamShapes.dynamicUnlockLinear.id,
      ].includes(shape.id)
    ) {
      if (!_.isNilOrEmptyString(unlock)) {
        if (
          guards.validateAmount({ t, value: unlock, max: amount }) !== undefined
        ) {
          return { state: "numeric", purpose: "steps", value: steps };
        }
        proofed.unlock = unlock;
      }
    }

    return { state: "ready", value: proofed };
  }, [fromChainId, isConnected, params, t]);
}

export function useAutofillPrepare() {
  const warning = useAutofillWarning();
  const { isConnecting } = useAccount();
  const { isLoading: isLoadingTokens } = useTokens();

  const isLoading = useMemo(
    () => isConnecting || isLoadingTokens,
    [isConnecting, isLoadingTokens],
  );

  const isPrepared = useMemo(
    () => !isLoading && warning.state === "ready",
    [isLoading, warning.state],
  );

  return {
    isLoading,
    isPrepared,
    warning,
  };
}
